import * as React from 'react';
import { UserState } from 'app/reducers/state';
import { executeQueryGetAuth } from 'app/utils/api';
import { Spinner, Callout, Intent, Tab, Tabs, Button, H3 } from '@blueprintjs/core';
import { each, find, clone, filter } from 'lodash';
import ReactTable from 'react-table';
import moment from 'moment';
import { PlayDetails } from '../PlayDetails';
import { launchDownloadLink } from 'app/utils/downloads';
import { DoneMarker } from '../DoneMarker';
import { NotDoneMarker } from '../NotDoneMarker';

import Highcharts from 'highcharts';
import ReactHighcharts from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import { formatDate } from 'app/utils';
import { SingleFeedbackView } from '../SingleFeedbackView';

highchartsMore(Highcharts);

interface GameData {
	postFeedback: string;
	preFeedback: string;
	userID: string;
	objectives: GameDataObjective[];
	submitted: string;
	"game-id": string;
	scenario: string;
	results: string;
}

interface GameDataObjective {
	academicDescription: string;
	category: string;
	description: string;
	name: string;
	result: string;
	submitted: string;
}

function sortStatBlock(stat: any) {
	// submitted
	
	// Handle undefined/null values
	if (!stat || !Array.isArray(stat)) {
		return stat;
	}

	stat.sort(function (a, b) {
		const aMoment = moment(a['submitted']);
		const bMoment = moment(b['submitted']);

		return !aMoment.isBefore(bMoment);
	});

	return stat;
}

function checkResultString(result: any) {
	return (result === true || result === "true" || result === "True")
}

function processStatByCategory(stat: GameData) {
	let categoryTotal = {};

	each(stat.objectives, objective => {
		if (categoryTotal[objective.category] === undefined) {
			categoryTotal[objective.category] = {
				total: 1,
				working: (checkResultString(objective.result)) ? 1 : 0
			}
		}
		else {
			categoryTotal[objective.category].working += (checkResultString(objective.result)) ? 1 : 0;
			categoryTotal[objective.category].total += 1;
		}

		categoryTotal[objective.category]["percent"] = (parseFloat(categoryTotal[objective.category].working) / parseFloat(categoryTotal[objective.category].total)) * 100.0;
	});

	return categoryTotal;
}

function getTotalValue(categoryTotal: any) {
	let total = 0.0;

	each(categoryTotal, cat => {
		total += cat.percent
	});

	return total;
}

function getBestPlaysForStudents(stats: GameData[]) {
	let userMap = {};

	each(stats, stat => {
		const userID = stat["user-id"];

		if (userMap[userID] === undefined) {
			userMap[userID] = stat;
			userMap[userID].categories = processStatByCategory(stat)
		}
		else {
			const newCategories = processStatByCategory(stat);

			const oldTotalForUser = getTotalValue(userMap[userID].categories);
			const newTotalForUser = getTotalValue(newCategories);

			if (newTotalForUser >= oldTotalForUser) {
				userMap[userID] = stat;
				userMap[userID].categories = newCategories;
			}
		}
	});

	return userMap;
}

function getInitialPlaysForStudents(stats: GameData[]) {
	let userMap = {};

	each(stats, stat => {
		const userID = stat["user-id"];

		if (userMap[userID] === undefined) {
			userMap[userID] = stat;
			userMap[userID].categories = processStatByCategory(stat)
		}
	});

	return userMap;
}

function getAllCategoryNames(stats: GameData[]) {
	let knownCategoryNames: string[] = [];

	each(stats, stat => {
		each(stat.objectives, objective => {
			if (knownCategoryNames.includes(objective.category) === false) {
				knownCategoryNames.push(objective.category);
			}
		});
	});

	return knownCategoryNames;
}

function flattenObjectiveResults(allCats, plays, classSize) {
	let totalValuesForBestPlay: number[] = [];

	let uniqueUsers: string[] = [];

	// Initialze this to the number of categories
	each(allCats, cat => {
		totalValuesForBestPlay.push(0);
	});

	each(plays, (play: any) => {
		const uid: string = play['user-id'];

		if (uniqueUsers.includes(uid) === false) {
			uniqueUsers.push(uid);
		}

		for (let i = 0; i < allCats.length; i++) {
			const categoryName = allCats[i];
			if (play.categories[categoryName]) {
				const playValueForThisCategory = play.categories[categoryName].percent;

				totalValuesForBestPlay[i] += playValueForThisCategory;
			}
		}
	});

	let correctClassSize = uniqueUsers.length;

	if (correctClassSize === 0) { correctClassSize = 1; }

	// average them all out
	for (let i = 0; i < totalValuesForBestPlay.length; i++) {
		if (totalValuesForBestPlay[i] !== 0) {
			totalValuesForBestPlay[i] = Math.round(totalValuesForBestPlay[i] / correctClassSize);
		}
	}

	return totalValuesForBestPlay;
}

interface Props {
	user: UserState;
	targetUserID: string;
	forceNoEdit: boolean;
	hideDownloadPrompt?: boolean;
	targetUserData?: any;
	showNavigation?: boolean;
	defaultSelectedTab?: string;
	goToNext?: () => void;
	goToPrevious?: () => void;
	chartMode?: boolean;
	chartScenario?: string;
};

interface State {
	needsClass: boolean;
	needsPayment: boolean;
	loadingOverview: boolean;
	loadingAllData: boolean;
	allData: any[];
	overviewData: any | undefined;
	blockFromEdit: boolean;
	selectedObjectiveSet: any | undefined;
	coachingData: any | undefined;
	selectedTranscriptGame: any | undefined;
}

export class SingleOverview extends React.Component<Props, State> {

	state = {
		needsClass: false,
		needsPayment: false,
		loadingOverview: false,
		loadingAllData: false,
		allData: [],
		overviewData: undefined,
		blockFromEdit: false,
		selectedObjectiveSet: undefined,
		coachingData: undefined,
		selectedTranscriptGame: undefined
	}

	componentDidMount() {
		this.setState({
			loadingOverview: true,
			loadingAllData: true
		});

		executeQueryGetAuth("progress/user/" + this.props.targetUserID + "/overview", {}, this.props.user, (data) => {
			this.setState({
				overviewData: data.body,
				loadingOverview: false
			});
		})

		executeQueryGetAuth("data/all/results/" + this.props.targetUserID, {}, this.props.user, (data) => {
			executeQueryGetAuth(`data/coaching/${this.props.targetUserID}`, {}, this.props.user, (response) => {
				try {
					const resp = JSON.parse(response.text);

					this.setState({
						allData: JSON.parse(data.text),
						loadingAllData: false,
						coachingData: resp
					})
				}
				catch (e) {

				}
			});
		});
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.targetUserID !== this.props.targetUserID) {
			this.setState({
				loadingOverview: true,
				loadingAllData: true,
				selectedObjectiveSet: undefined
			});

			executeQueryGetAuth("progress/user/" + this.props.targetUserID + "/overview", {}, this.props.user, (data) => {
				this.setState({
					overviewData: data.body,
					loadingOverview: false
				});
			})

			executeQueryGetAuth("data/all/results/" + this.props.targetUserID, {}, this.props.user, (data) => {
				executeQueryGetAuth(`data/coaching/${this.props.targetUserID}`, {}, this.props.user, (response) => {
					try {
						const resp = JSON.parse(response.text);

						this.setState({
							allData: JSON.parse(data.text),
							loadingAllData: false,
							coachingData: resp
						})
					}
					catch (e) {

					}
				});
			});
		}
	}

	getIsInChartMode = () => {
		if (this.props.chartMode === true) {
			return true;
		}

		return false;
	}

	handleSelectPayload = (gameID: string) => {
		const {
			allData
		} = this.state;

		const dataEntry = find(allData!, (entry: any) => {
			return entry["game-id"] === gameID
		});

		const block = gameID === this.getScenarioOverview(dataEntry['scenario'])['initial-game-id'];

		this.setState({
			selectedObjectiveSet: dataEntry,
			blockFromEdit: block
		});

		window.scrollTo(0, 0);
	}

	getScenarioBestPlayData = (scenarioName: string) => {
		const {
			allData
		} = this.state;

		const scenarioOverview = this.getScenarioOverview(scenarioName);

		const dataEntry = find(allData!, (entry: any) => {
			return entry["game-id"] === scenarioOverview["best-game-id"]
		});

		return dataEntry;
	}

	getScenarioInitialPlayData = (scenarioName: string) => {
		const {
			allData
		} = this.state;

		const scenarioOverview = this.getScenarioOverview(scenarioName);

		const dataEntry = find(allData!, (entry: any) => {
			return entry["game-id"] === scenarioOverview["initial-game-id"]
		});

		return dataEntry;
	}

	getScenarioOverview = (scenarioName: string) => {
		const {
			loadingOverview,
			overviewData
		} = this.state;

		if (loadingOverview) return {}

		if (overviewData === undefined) return {}

		return overviewData![scenarioName];
	}

	getScenarioBestScore = (scenarioName: string) => {
		const scenarioOverview = this.getScenarioOverview(scenarioName);

		if (scenarioOverview["best-max"] === "" || scenarioOverview["best-max"] === undefined || scenarioOverview["best-max"] === "0") {
			return "Not Played";
		}

		const bestOutOf = parseFloat(scenarioOverview["best-max"]);
		const bestScore = parseFloat(scenarioOverview["best-score"]);

		const percentageScore = (bestScore / bestOutOf) * 100.0;

		return <span>
			{Math.round(percentageScore)}
			{"%"}
		</span>
	}

	// Return a graphic if the total-plays is atleast 1
	getScenarioHasPlayed = (scenarioName: string) => {
		const scenarioOverview = this.getScenarioOverview(scenarioName);

		if (parseInt(scenarioOverview['total-plays']) >= 1) {
			return <DoneMarker />
		}
		else {
			return <NotDoneMarker />
		}
	}

	getTutorialHasPlayed = () => {
		const ov = this.getScenarioOverview("scene_1_bc");

		if (ov["total-plays"] >= 1) {
			return <DoneMarker />
		}

		return <NotDoneMarker />
	}

	getScenarioInitilalScore = (scenarioName: string) => {
		const scenarioOverview = this.getScenarioOverview(scenarioName);

		if (scenarioOverview["best-max"] === "" || scenarioOverview["best-max"] === undefined || scenarioOverview["best-max"] === "0") {
			return "Not Played";
		}

		const bestOutOf = parseFloat(scenarioOverview["best-max"]);
		const bestScore = parseFloat(scenarioOverview["initial-score"]);

		const percentageScore = (bestScore / bestOutOf) * 100.0;

		return <span>
			{Math.round(percentageScore)}
			{"%"}
		</span>
	}


	renderOverviewCalloutEntry = (label: string, value: any) => {
		return <tr>
			<td style={{ width: '400px' }}>{label}</td>
			<td>{value}</td>
		</tr>
	}

	getDataByScenario = (scenarioName: string) => {
		const {
			allData
		} = this.state;

		//@ts-ignore
		if (allData === undefined || (Array.isArray(allData) && allData.length === 0)) return undefined;

		let filteredResults: any[] = [];
		let gameIDAlreadyTracked: any[] = [];

		each(allData, (dataEntry: any) => {
			if (dataEntry.scenario === scenarioName) {
				if (gameIDAlreadyTracked.includes(dataEntry['game-id']) === false) {
					gameIDAlreadyTracked.push(dataEntry['game-id']);
					filteredResults.push(dataEntry);
				}
			}
		});

		return filteredResults;
	}

	updateJournalDataInline = (stage: string, payload: string, gameID: string) => {
		const {
			allData
		} = this.state;

		let newAllData: any[] = [];

		each(allData, (dataEntry: any) => {
			if (dataEntry['game-id'] === gameID) {
				if (stage === "pre") {
					dataEntry['preFeedback'] = payload;
				}
				else {
					dataEntry['postFeedback'] = payload;
				}
			}

			newAllData.push(dataEntry);
		});

		this.setState({
			allData: newAllData
		});
	}

	convertScenarioToFullName = (scenario: string) => {
		if (scenario === "Coaching") {
			return "Coaching";
		}

		if (scenario === 'tutorial') {
			return "Tutorial";
		}

		if (scenario === 'scenario_1') {
			return "Scenario 1 Explore";
		}

		if (scenario === 'scenario_1_initial') {
			return "Scenario 1 Initial";
		}

		if (scenario === 'scenario_1_assess') {
			return "Leadership Styles Assess Mode"; 
		}

		if (scenario === 'scenario_1_a1') {
			return "Directive Lesson";
		}

		if (scenario === 'scenario_1_a1_initial') {
			return "Leadership Styles Initial Play";
		}

		if (scenario === 'scenario_1_a2') {
			return "Delegative Lesson";
		}

		if (scenario === 'scenario_1_a3') {
			return "Participative Lesson";
		}

		if (scenario === 'scenario_2_initial') {
			return "Scenario 2 Initial";
		}

		if (scenario === 'scenario_2') {
			return "Scenario 2 Explore";
		}

		if (scenario === 'scenario_2_assess') {
			return "Scenario 2 Assess";
		}

		if (scenario === 'scenario_2_a1') {
			return "Competing";
		}

		if (scenario === 'scenario_2_a2') {
			return "Accommodating";
		}

		if (scenario === 'scenario_2_a3') {
			return "Compromising";
		}

		if (scenario === 'scenario_3_initial') {
			return "Scenario 3 Initial";
		}

		if (scenario === 'scenario_3') {
			return "Scenario 3 Explore";
		}

		if (scenario === 'scenario_3_assess') {
			return "Scenario 3 Assess";
		}

		if (scenario === 'scenario_5_initial') {
			return "Scenario 5 Initial";
		}

		if (scenario === 'scenario_5') {
			return "Scenario 5 Explore";
		}

		if (scenario === 'scenario_5_assess') {
			return "Scenario 5 Assess";
		}


		if (scenario === 'scenario_3_a1') {
			return "Forming";
		}

		if (scenario === 'scenario_3_a2') {
			return "Storming";
		}

		if (scenario === 'scenario_3_a3') {
			return "Norming";
		}

		if (scenario === 'scene_1_bc') {
			return "Get the Right Work Done";
		}

		if (scenario === 'scene_2_bc') {
			return "Getting Access to Hidden Ideas";
		}

		if (scenario === 'scene_4_bc') {
			return "Getting Commitment";
		}
	}

	renderTutorialTabPanel = () => {

	}

	renderOverviewTabPanel = (scenarioName: string) => {
		const {
			loadingAllData
		} = this.state;

		const allData = sortStatBlock(this.getDataByScenario(scenarioName));

		//@ts-ignore
		if (loadingAllData || allData === undefined || (Array.isArray(allData) && allData.length === 0)) {
			return <Spinner />
		}

		const playColumns = [{
			Header: "Scenario",
			accessor: 'scenario',
			Cell: (props) => {
				return <span>{this.convertScenarioToFullName(props.value)}</span>
			}
		}, {
			Header: "Played",
			accessor: "submitted",
			Cell: (props) => {
				return <span>
					{formatDate(props.value)}
				</span>
			}
		}, {
			Header: "Score",
			accessor: "results",
			Cell: (props) => {
				try {
					const valueAsDecimal = parseFloat(props.value) * 100.0;
					const asWholeNumber = Math.round(valueAsDecimal);

					return <span>{asWholeNumber}%</span>
				}
				catch (e) {
					return <span>-</span>
				}
			}
		}, {
			Header: "",
			accessor: "game-id",
			Cell: props => {
				if (props.value === "") {
					return <span></span>
				}

				return this.renderNormalButton(undefined, () => {
					this.handleSelectPayload(props.value);
					// scroll to top of page
					window.scrollTo(0, 0);
				}, Intent.PRIMARY, "View Details", false);
			}
		}]

		return <div>
			<ReactTable data={allData} pageSize={allData.length} columns={playColumns} showPagination={false} showPageSizeOptions={false} />
		</div>
	}

	renderNormalButton = (rightIcon: any, onClick: any, intent: any, text: string, disabled: boolean) => {
		return <Button disabled={disabled} style={{ margin: '0px', width: '100%' }} rightIcon={rightIcon} onClick={() => { onClick(); }} intent={intent} text={text} />
	}

	getCanEditJournal = () => {
		const {
			forceNoEdit
		} = this.props;

		if (forceNoEdit) return false;

		if (this.state.blockFromEdit) return false;

		return true;
	}

	getTranscriptData = () => {
		const {
			allData,
			coachingData
		} = this.state;

		//@ts-ignore
		if ((Array.isArray(allData) && allData.length === 0) || allData === undefined) return [];

		let data: any[] = clone(allData);

		let matches: any[] = [];

		// Remove rogue game entries
		data = filter(data, (entry) => {
			const matchFound = matches.includes(entry['game-id']);
			if (matchFound === true) return false;
			matches.push(entry['game-id']);
			return entry['game-id'] !== null;
		});

		if (coachingData !== undefined) {
			//@ts-ignore
			for (let i = 0; i < coachingData!.length; i++) {
				//@ts-ignore
				const _coaching = coachingData[i]['coaching'];
				//@ts-ignore
				const _created = coachingData[i]['created'];
				//@ts-ignore
				let _viewed = false;

				try {
					//@ts-ignore
					if (coachingData[i]['viewed'] === 1 || coachingData[i]['viewed'] === "1") {
						_viewed = true;
					}
				}
				catch (e) {

				}

				data.push(
					{
						submitted: _created,
						scenario: 'Coaching',
						results: '-',
						preFeedback: 'Coaching Recieved: ' + _coaching,
						postFeedback: _viewed ? 'Viewed' : 'Has Not Been Viewed',
						'game-id': ''
					}
				)
			}
		}

		try {
			data.sort((a: any, b: any) => {
				const aMoment = moment(a['submitted']);
				const bMoment = moment(b['submitted']);

				return aMoment.isBefore(bMoment) ? -1 : 1;
			});
		}
		catch (e) {
			// If data is a datatype that doesn't support .sort, that we missed
			// in our previous check then just try to gracifully fall back out of
			// this situation.
			return [];
		}

		return data;
	}

	renderTranscriptTab = () => {
		const allData = this.getTranscriptData();

		const playColumns = [{
			Header: "Scenario",
			accessor: 'scenario',
			width: 150,
			Cell: (props) => {
				return <span>{this.convertScenarioToFullName(props.value)}</span>
			}
		}, {
			Header: "Played",
			accessor: "submitted",
			width: 150,
			Cell: (props) => {
				return <span>
					{formatDate(props.value)}
				</span>
			},
			sortMethod: (a, b) => {
				if (a === b) {
					return 0;
				}

				const _a = moment(a);
				const _b = moment(b);

				return _a.isBefore(_b);
			}
		}, {
			Header: "Score",
			accessor: "results",
			width: 70,
			Cell: (props) => {
				if (props.value === "-") {
					return <span>-</span>
				}
				try {
					const valueAsDecimal = parseFloat(props.value) * 100.0;
					const asWholeNumber = Math.round(valueAsDecimal);

					return <span className='score-circle'>{asWholeNumber}%</span>
				}
				catch (e) {
					return <span className='score-circle'>-</span>
				}
			}
		}, {
			Header: "Pre Feedback",
			accessor: "preFeedback",
			Cell: props => {
				const feedback = props.value;

				return <SingleFeedbackView feedback={feedback} />
			}
		}, {
			Header: "Post Feedback",
			accessor: "postFeedback",
			Cell: props => {
				const feedback = props.value;

				return <SingleFeedbackView feedback={feedback} />
			}
		},{
			Header: "",
			accessor: "game-id",
			Cell: props => {
				return this.renderNormalButton(undefined, () => {
					this.setState({
						selectedTranscriptGame: <PlayDetails updateJournalDataInline={this.updateJournalDataInline} canEditJournal={this.getCanEditJournal()} selectedGame={props.row._original} user={this.props.user} />
					})
				}, Intent.PRIMARY, "View Details", false);
			}
		}];

		return <div>
			{this.state.selectedTranscriptGame}
			<ReactTable
				className="transcriptTable"
				data={allData}
				pageSize={allData ? allData!.length : 0}
				//@ts-ignore
				columns={playColumns}
				showPagination={false}
				getTrProps={(state, rowInfo, column) => {
					return {
						style: {
							minHeight: '66px'
						}
					}
				}}
				defaultSorted={[{
					id: 'submitted',
					desc: true,
				}]}
				showPageSizeOptions={false}
			/>
		</div>
	}

	getStatsForScenario = (scenarioName: string) => {
		const {
			allData
		} = this.state;

		return filter(allData, stat => {
			//@ts-ignore
			return stat.scenario === scenarioName
		});
	}

	renderScenarioSpecificRadarChart = (scenario: string) => {
		const stats = this.getStatsForScenario(scenario + "_initial");
		const allCats = getAllCategoryNames(stats);

		const initialPlays = getInitialPlaysForStudents(stats);
		const totalValuesForInitalPlay = flattenObjectiveResults(allCats, initialPlays, 1);


		const bestExploreStats = this.getStatsForScenario(scenario);
		const bestAssessStats = this.getStatsForScenario(scenario + "_assess");
		const combinedBestStats = bestAssessStats.concat(bestExploreStats);

		const bestAsssessPlays = getBestPlaysForStudents(combinedBestStats);
		const totalValuesForAssessPlay = flattenObjectiveResults(allCats, bestAsssessPlays, 1);

		if (totalValuesForInitalPlay.length === 0 || allCats.length === 0) {
			return <span />
		}

		let chartConfig: Highcharts.Options = {
			chart: {
				polar: true,
				height: 400
			},
			pane: {
				startAngle: 0,
				endAngle: 360
			},
			title: {
				text: 'Initial Play compared to Best Explore Play'
			},
			subtitle: {
				text: 'Student Growth'
			},
			xAxis: {
				tickInterval: 1,
				min: 0,
				max: allCats.length,
				categories: allCats
			},
			plotOptions: {
				series: {
					pointStart: 0,
					pointInterval: 1,
				},
			},
			credits: {
				enabled: false
			},
			yAxis: {
				min: 0,
				max: 100
			},
			series: [{
				name: 'Initial Play',
				type: 'area',
				data: totalValuesForInitalPlay
			}, {
				name: "Best Explore Play",
				type: 'area',
				data: totalValuesForAssessPlay
			}]
		}

		return <span><ReactHighcharts highcharts={Highcharts} options={chartConfig} /></span>
	}

	getUserName = () => {
		const {
			user,
			targetUserData
		} = this.props;

		let _user = user;

		if (targetUserData !== undefined && targetUserData !== null) {
			_user = targetUserData;
		}

		if (_user === undefined || _user === null) { return ''; }

		return `${_user.first} ${_user.last}`;
	}

	renderOverviewTab = () => {
		const {
			selectedObjectiveSet,
		} = this.state;

		const {
			user
		} = this.props;

		const tutorialDataSet = [{
			title: "Getting Started",
			val: this.getTutorialHasPlayed(),
			button: undefined
		},
		{
			title: "Getting Work Done",
			val: this.getScenarioBestScore("scene_1_bc"),
			button: this.getScenarioOverview("scene_1_bc")["best-game-id"]
		},
		{
			title: "Getting Access to Hidden Ideas",
			val: this.getScenarioBestScore("scene_2_bc"),
			button: this.getScenarioOverview("scene_2_bc")["best-game-id"]
		},
		{
			title: "Getting Commitment",
			val: this.getScenarioBestScore("scene_4_bc"),
			button: this.getScenarioOverview("scene_4_bc")["best-game-id"]
		}];

		const scenario1DataSet = [{
			title: "Scenario 1 Initial Play",
			val: this.getScenarioInitilalScore("scenario_1_initial"),
			button: this.getScenarioOverview("scenario_1_initial")["initial-game-id"]
		}, {
			title: "Scenario 1 Best Explore Play",
			val: this.getScenarioBestScore("scenario_1"),
			button: this.getScenarioOverview("scenario_1")["best-game-id"]
		}, {
			title: "Scenario 1 Best Assess Play",
			val: this.getScenarioBestScore("scenario_1_assess"),
			button: this.getScenarioOverview("scenario_1_assess")["best-game-id"]
		}, {
			title: "Directive Lesson",
			val: this.getScenarioHasPlayed("scenario_1_a1"),
			button: this.getScenarioOverview("scenario_1_a1")["best-game-id"]
		}, {
			title: "Delegative Lesson",
			val: this.getScenarioHasPlayed("scenario_1_a2"),
			button: this.getScenarioOverview("scenario_1_a2")["best-game-id"]
		}, {
			title: "Participative Lesson",
			val: this.getScenarioHasPlayed("scenario_1_a3"),
			button: this.getScenarioOverview("scenario_1_a3")["best-game-id"]
		}];

		const scenario2DataSet = [{
			title: "Scenario 2 Initial Play",
			val: this.getScenarioInitilalScore("scenario_2_initial"),
			button: this.getScenarioOverview("scenario_2_initial")["initial-game-id"]
		}, {
			title: "Scenario 2 Best Explore Play",
			val: this.getScenarioBestScore("scenario_2"),
			button: this.getScenarioOverview("scenario_2")["best-game-id"]
		}, {
			title: "Scenario 2 Best Assess Play",
			val: this.getScenarioBestScore("scenario_2_assess"),
			button: this.getScenarioOverview("scenario_2_assess")["best-game-id"]
		}, {
			title: "Competing",
			val: this.getScenarioHasPlayed("scenario_2_a1"),
			button: this.getScenarioOverview("scenario_2_a1")["best-game-id"]
		}, {
			title: "Accommodating",
			val: this.getScenarioHasPlayed("scenario_2_a2"),
			button: this.getScenarioOverview("scenario_2_a2")["best-game-id"]
		}, {
			title: "Compromising",
			val: this.getScenarioHasPlayed("scenario_2_a3"),
			button: this.getScenarioOverview("scenario_2_a3")["best-game-id"]
		}];

		const scenario3DataSet = [{
			title: "Scenario 3 Initial Play",
			val: this.getScenarioInitilalScore("scenario_3_initial"),
			button: this.getScenarioOverview("scenario_3_initial")["initial-game-id"]
		}, {
			title: "Scenario 3 Best Explore Play",
			val: this.getScenarioBestScore("scenario_3"),
			button: this.getScenarioOverview("scenario_3")["best-game-id"]
		}, {
			title: "Scenario 3 Best Assess Play",
			val: this.getScenarioBestScore("scenario_3_assess"),
			button: this.getScenarioOverview("scenario_3_assess")["best-game-id"]
		}, {
			title: "Forming",
			val: this.getScenarioHasPlayed("scenario_3_a1"),
			button: this.getScenarioOverview("scenario_3_a1")["best-game-id"]
		}, {
			title: "Storming",
			val: this.getScenarioHasPlayed("scenario_3_a2"),
			button: this.getScenarioOverview("scenario_3_a2")["best-game-id"]
		}, {
			title: "Norming",
			val: this.getScenarioHasPlayed("scenario_3_a3"),
			button: this.getScenarioOverview("scenario_3_a3")["best-game-id"]
		}];

		const scenarioOverviewColumnDef = [{
			Header: "",
			accessor: "title"
		}, {
			Header: "",
			accessor: "val"
		}, {
			Header: "",
			accessor: "button",
			Cell: (props) => {
				const disabled = (props.value === "" || props.value === undefined);

				return this.renderNormalButton(undefined, () => {
					this.handleSelectPayload(props.value);
				}, Intent.PRIMARY, "View Details", disabled);
			}
		}]


		return <span style={{ width: '50%', display: 'block', margin: 'auto' }}>
			<H3>{this.getUserName()}</H3>
			<span style={{ display: 'block', marginBottom: '15px' }}>{formatDate(new Date().toUTCString())}</span>
			{selectedObjectiveSet ? <PlayDetails updateJournalDataInline={this.updateJournalDataInline} canEditJournal={this.getCanEditJournal()} selectedGame={selectedObjectiveSet!} user={user} /> : undefined}
			<Callout intent={Intent.NONE} title={"Intent-based Communication"}>
				<ReactTable className={"minimal-overview-table"} data={tutorialDataSet} pageSize={tutorialDataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
			</Callout>

			<Callout intent={Intent.NONE} title={"Transforming Relationships"}>
				<ReactTable className={"minimal-overview-table"} data={scenario1DataSet} pageSize={scenario1DataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
				{this.renderScenarioSpecificRadarChart('scenario_1')}
			</Callout>

			<Callout style={{ marginTop: '10px' }} intent={Intent.NONE} title={"Opportunities in Conflict"}>
				<ReactTable className={"minimal-overview-table"} data={scenario2DataSet} pageSize={scenario2DataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
				{this.renderScenarioSpecificRadarChart('scenario_2')}
			</Callout>

			<Callout style={{ marginTop: '10px' }} intent={Intent.NONE} title={"Facilitating Change"}>
				<ReactTable className={"minimal-overview-table"} data={scenario3DataSet} pageSize={scenario3DataSet.length} showPageSizeOptions={false} showPagination={false} sortable={false} columns={scenarioOverviewColumnDef} />
				{this.renderScenarioSpecificRadarChart('scenario_3')}
			</Callout>

			<Callout style={{ marginTop: '10px' }} intent={Intent.NONE} title={"View All Your Plays"}>
				<div className="fullTabSize">
					<Tabs
						animate={true}
						id="score_view_tabs"
						key={"horizontal"}
						renderActiveTabPanelOnly={false}
						vertical={false}
					>
						<Tab id="tb_scenario_1_bc1" title="Get the Right Work Done" panel={this.renderOverviewTabPanel("scene_1_bc")} />
						<Tab id="tb_scenario_1_bc2" title="Getting Access to Hidden Ideas" panel={this.renderOverviewTabPanel("scene_2_bc")} />
						<Tab id="tb_scenario_1_bc3" title="Getting Commitment" panel={this.renderOverviewTabPanel("scene_4_bc")} />
						<Tab id="tb_scenario_1" title="Scenario 1 Explore" panel={this.renderOverviewTabPanel("scenario_1")} />
						<Tab id="tb_scenario_1_a" title="Scenario 1 Assess" panel={this.renderOverviewTabPanel("scenario_1_assess")} />
						<Tab id="tb_scenario_1_a1" title="Directive Lesson" panel={this.renderOverviewTabPanel("scenario_1_a1")} />
						<Tab id="tb_scenario_1_a2" title="Delegative Lesson" panel={this.renderOverviewTabPanel("scenario_1_a2")} />
						<Tab id="tb_scenario_1_a3" title="Participative Lesson" panel={this.renderOverviewTabPanel("scenario_1_a3")} />
						<Tab id="tb_scenario_2" title="Scenario 2 Explore" panel={this.renderOverviewTabPanel("scenario_2")} />
						<Tab id="tb_scenario_2_a" title="Scenario 2 Assess" panel={this.renderOverviewTabPanel("scenario_2_assess")} />
						<Tab id="tb_scenario_2_a1" title="Competing" panel={this.renderOverviewTabPanel("scenario_2_a1")} />
						<Tab id="tb_scenario_2_a2" title="Accommodating" panel={this.renderOverviewTabPanel("scenario_2_a2")} />
						<Tab id="tb_scenario_2_a3" title="Compromising" panel={this.renderOverviewTabPanel("scenario_2_a3")} />
						<Tab id="tb_scenario_3" title="Scenario 3 Explore" panel={this.renderOverviewTabPanel("scenario_3")} />
						<Tab id="tb_scenario_3_a" title="Scenario 3 Assess" panel={this.renderOverviewTabPanel("scenario_3_assess")} />
						<Tab id="tb_scenario_3_a1" title="Forming" panel={this.renderOverviewTabPanel("scenario_3_a1")} />
						<Tab id="tb_scenario_3_a2" title="Storming" panel={this.renderOverviewTabPanel("scenario_3_a2")} />
						<Tab id="tb_scenario_3_a3" title="Norming" panel={this.renderOverviewTabPanel("scenario_3_a3")} />
					</Tabs>
				</div>
			</Callout>
		</span>
	}

	render() {
		const {
			loadingAllData,
			loadingOverview
		} = this.state;

		if (loadingAllData || loadingOverview) {
			return <div style={{margin: '15px'}}><Spinner /></div>
		}

		const {
			hideDownloadPrompt
		} = this.props;

		const shouldShowDownloadPrompt = (hideDownloadPrompt === true) ? false : true;

		if (this.getIsInChartMode()) {
			return <span>
				{this.renderScenarioSpecificRadarChart(this.props.chartScenario)}
			</span>
		}

		return <span>
			{shouldShowDownloadPrompt ?
				<Callout style={{ marginBottom: '20px' }} intent={Intent.PRIMARY} title="Download Your Software">
					If you are looking for the software you can download it here!
					<Button style={{ float: 'right', width: '200px' }} icon={"download"} large={true} text="Download" intent={Intent.PRIMARY} onClick={() => {
						launchDownloadLink();
					}} />
					<div className="clearfix" />
				</Callout> : undefined}

			<Tabs
				animate={true}
				id="overview_navigation"
				key={"horizontal2"}
				renderActiveTabPanelOnly={false}
				vertical={false}
				defaultSelectedTabId={this.props.defaultSelectedTab}
			>
				<Tab id="overview_nav_1" title="Score Overview" panel={this.renderOverviewTab()} />
				<Tab id="overview_nav_2" title="Transcript"  panel={this.renderTranscriptTab()} />
			</Tabs>

		</span>
	}
}
